import { FocusTrap } from "@mantine/core";
import { useCallback } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { CreateProcessStep } from "#modules/processSteps/infra/createProcessStep.api";
import { useCreateProcessStep } from "#modules/processSteps/view/useCreateProcessStep";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { PartialProcessStep } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStep";
import { useProcessStepsSelect } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/useProcessStepsSelect";
import { useAlertChip } from "#modules/quoteRequestLines/views/useAlertChip";
import { Flex } from "#shared/view/components/Flex";
import { CloseButton } from "#shared/view/designSystem/CloseButton";
import { Select } from "#shared/view/designSystem/Select";
import {
  IndustrialProcessSchema,
  IndustrialProcessType,
  ProcessStepModeSchema,
  QuoteRequestLineDto,
  isOutsourcingProcessStep,
} from "shared";

import { ProcessStepsTable } from "./ProcessSteps/ProcessStepsTable";

const filterOutsourcingProcessSteps = <T extends { industrialProcess: IndustrialProcessType }>(
  processSteps: T[],
) =>
  processSteps.filter(
    (ps) =>
      isOutsourcingProcessStep(ps) ||
      ps.industrialProcess === IndustrialProcessSchema.enum.TRANSPORT,
  );

type Props = {
  processStepSelectIsDisplayed: boolean;
  hideProcessStepSelect: () => void;
  quoteRequestLine?: QuoteRequestLineDto;
};

export const OutsourcingSection = ({
  processStepSelectIsDisplayed,
  hideProcessStepSelect,
  quoteRequestLine,
}: Props) => {
  const { t } = useTranslation();
  const { control } = useFormContext<QuoteRequestLineFormData>();

  const quoteRequestLineId = useWatch({ control, name: "quoteRequestLineId" });
  const processSteps = useWatch({ control, name: "processSteps" });

  const { alerts } = useAlertChip(quoteRequestLine);

  const { fields: outsourcingProcessSteps, append: appendProcessStep } = useFieldArray({
    control,
    name: "outsourcingProcessSteps",
    keyName: "__rhfId",
  });

  const { createProcessStep } = useCreateProcessStep(undefined, {
    onSuccess: (processStep: CreateProcessStep) => {
      appendProcessStep(processStep);
      hideProcessStepSelect();
    },
  });

  const { allProcessStepsDataForSelect, getProcessStepToAddFromSelect } = useProcessStepsSelect(
    filterOutsourcingProcessSteps,
  );

  const computeNewIndex = useCallback(() => {
    const indexes = processSteps?.map((ps) => ps.index);

    if (!indexes || indexes.length === 0) {
      return 0;
    }

    return Math.max(...indexes) + 1;
  }, [processSteps]);

  const addProcessStep = (value: string | null) => {
    const processSteptoAdd = getProcessStepToAddFromSelect(value);

    const newIndex = computeNewIndex();
    if (!processSteptoAdd) {
      return;
    }

    const getValues = <K extends keyof PartialProcessStep>(
      processStep: PartialProcessStep,
      key: K,
    ): PartialProcessStep[K] | null => {
      return key in processStep ? processStep[key] : null;
    };

    const payload = {
      quoteRequestLineId: quoteRequestLineId,
      industrialProcess: processSteptoAdd.industrialProcess,
      mode: processSteptoAdd.mode || ProcessStepModeSchema.enum.TIME,
      manHourlyPrice: getValues(processSteptoAdd, "manHourlyPrice") || 0,
      manTime: getValues(processSteptoAdd, "manTime") || 0,
      fixedCost: getValues(processSteptoAdd, "fixedCost") || 0,
      fixedCostPerSeries: getValues(processSteptoAdd, "fixedCostPerSeries") || undefined,
      margin: getValues(processSteptoAdd, "margin") || 0,
      machinePreparationTime: getValues(processSteptoAdd, "machinePreparationTime") || 0,
      machineHourlyPrice: getValues(processSteptoAdd, "machineHourlyPrice") || 0,
      otherMachineTime: getValues(processSteptoAdd, "otherMachineTime") || 0,
      index: newIndex,
      cycleTimeCost: getValues(processSteptoAdd, "cycleTimeCost") || undefined,
      machinePreparationTimeCost:
        getValues(processSteptoAdd, "machinePreparationTimeCost") || undefined,
      description: processSteptoAdd.description || undefined,
      isOutsourced: true,
    };
    createProcessStep({
      payload,
    });
  };

  return (
    <>
      {processStepSelectIsDisplayed && (
        <FocusTrap>
          <Flex align={"center"} justify={"space-between"} pb={"xs"}>
            <Select
              data-testid="process-step-select"
              placeholder={t("quoteRequestLine.outsourcing.selectPlaceholder")}
              data={allProcessStepsDataForSelect}
              w={"100%"}
              searchable
              data-autofocus
              onChange={addProcessStep}
              value={""}
            />

            <CloseButton size="sm" onClick={hideProcessStepSelect} />
          </Flex>
        </FocusTrap>
      )}
      <ProcessStepsTable
        showCompanyMachines={false}
        processSteps={outsourcingProcessSteps}
        alerts={alerts}
      />
    </>
  );
};
